import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import "./Home.css";
import "./Marketing.css";
import { InviteForm, Bubble, SiteNav, SiteFooter, useSeo } from "./_site";

export default function HowItWorks() {
  useSeo(
    "How Remy Works | Just Text Remy",
    "See how Remy helps you keep up with email, your calendar, reminders and everyday life through a simple text conversation."
  );

  return (
    <div className="remy-home" data-testid="how-it-works-page">
      <SiteNav />

      {/* HERO */}
      <header className="mk-phero" id="top">
        <div className="rh-wrap mk-phero__grid">
          <div className="rh-hero__copy mk-hero-copy">
            <p className="rh-eyebrow">How it works</p>
            <h1 className="rh-hdtext" data-testid="how-hero-heading">Remy is as easy as sending a text.</h1>
            <p className="rh-subhead rh-subhead--sm">No new routine to learn.</p>
            <p className="rh-body">Just text Remy and get on with your day.</p>
            <div className="mk-cta-row">
              <a href="#invite" className="rh-btn" data-testid="how-hero-cta">Request an invite <ArrowRight size={18} /></a>
            </div>
            <p className="mk-hero__secondary">Private access. We're opening Remy gradually.</p>
          </div>
          <div className="mk-convo" data-testid="how-hero-convo">
            <span className="mk-convo__cap">Messages · Remy</span>
            <div className="rh-bubbles">
              <Bubble who="me">Hey Remy, what's first today?</Bubble>
              <Bubble who="remy">Start with the 9:30 call. I'll remind you ten minutes before.</Bubble>
              <Bubble who="me">Perfect, thanks.</Bubble>
            </div>
          </div>
        </div>
      </header>

      {/* STEPS */}
      <section className="rh-section" id="steps">
        <div className="rh-wrap">
          <div className="mk-head">
            <p className="rh-kicker">Four simple steps</p>
            <h2 className="mk-h2">From hello to handled.</h2>
          </div>
          <div className="mk-steps">
            <div className="mk-step mk-step--plain">
              <div>
                <div className="mk-step__num">01</div>
                <h3 className="mk-step__title">Get invited</h3>
                <p className="mk-step__body">
                  Request access with your name and mobile number. We're opening Remy gradually while we
                  refine the experience.
                </p>
                <div className="mk-chips"><span className="mk-chip">Private access</span></div>
              </div>
            </div>

            <div className="mk-step mk-step--plain">
              <div>
                <div className="mk-step__num">02</div>
                <h3 className="mk-step__title">Set up Remy</h3>
                <p className="mk-step__body">Verify your phone and choose which services you want to connect.</p>
                <div className="mk-chips">
                  <span className="mk-chip">Your phone</span>
                  <span className="mk-chip">Connected services</span>
                  <span className="mk-chip">Your preferences</span>
                </div>
              </div>
            </div>

            <div className="mk-step">
              <div className="mk-step__copy">
                <div className="mk-step__num">03</div>
                <h3 className="mk-step__title">Text Remy</h3>
                <p className="mk-step__body">
                  Ask about your calendar, email, reminders, or anything else you need help keeping track of.
                </p>
              </div>
              <div className="rh-bubbles">
                <Bubble who="me">What do I need to handle today?</Bubble>
                <Bubble who="remy">Two things matter today: your invoice is due, and you have a 3 PM appointment. Everything else can wait.</Bubble>
              </div>
            </div>

            <div className="mk-step">
              <div className="mk-step__copy">
                <div className="mk-step__num">04</div>
                <h3 className="mk-step__title">Remy keeps up</h3>
                <p className="mk-step__body">
                  Remy follows the conversation, remembers useful context, and brings important things back
                  when they matter.
                </p>
              </div>
              <div className="rh-bubbles">
                <Bubble who="me">What do I have tomorrow?</Bubble>
                <Bubble who="remy">A dentist appointment at 2 PM and a call at 4.</Bubble>
                <Bubble who="me">What about after 5?</Bubble>
                <Bubble who="remy">You're clear after 5:30.</Bubble>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIMPLE VALUE */}
      <section className="rh-section">
        <div className="rh-wrap">
          <div className="mk-head">
            <h2 className="mk-h2">No dashboards to babysit.</h2>
            <p className="rh-lead">No complicated setup. No new system to manage. Just text Remy.</p>
          </div>
          <div className="mk-trio">
            <div className="mk-trio__item"><b>Text it</b><span>Ask naturally.</span></div>
            <div className="mk-trio__item"><b>Remember it</b><span>Remy can keep useful context.</span></div>
            <div className="mk-trio__item"><b>Handle it</b><span>Let Remy help you keep track of what matters.</span></div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="rh-section mk-trust">
        <div className="rh-wrap">
          <div className="mk-head">
            <p className="rh-kicker">Your call</p>
            <h2 className="mk-h2 mk-h2--red">You decide what Remy knows.</h2>
            <p className="rh-lead">
              Connect only the services you want. Share only what you choose. Remy's access is tied to your
              account and permissions.
            </p>
            <ul className="mk-trust__points">
              <li><Check size={20} /> Your connections are optional.</li>
              <li><Check size={20} /> Your account stays separate from other users.</li>
              <li><Check size={20} /> Unknown phone numbers do not get access to your private information.</li>
            </ul>
            <Link to="/privacy" className="mk-link" data-testid="how-privacy-link">Learn more about Privacy <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="rh-section rh-private" id="invite">
        <div className="rh-wrap rh-private__grid">
          <div className="rh-private__copy">
            <p className="rh-kicker">Private access</p>
            <h2 className="rh-want">Want in?</h2>
            <p className="rh-lead">
              We're opening Remy slowly so we can make the experience feel right before opening it more
              broadly.
            </p>
          </div>
          <div className="rh-private__card">
            <InviteForm idPrefix="how" variant="card" source="how_it_works" />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
